import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Users,
  Headphones,
  BadgeIndianRupee,
} from "lucide-react";
import { WHY_US } from "../data/site";

const ICONS = { ShieldCheck, Users, Headphones, BadgeIndianRupee };

export default function WhyChooseUs() {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      {/* Background image with glass overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/32763065/pexels-photo-32763065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Why choose us"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-ocean/85 via-slate-900/80 to-slate-900/90" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-xs uppercase tracking-[0.24em] text-brand-orange font-semibold">
            Why Travellers Choose Us
          </div>
          <h2
            data-testid="why-heading"
            className="mt-3 font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight text-balance"
          >
            Genuine people. Honest pricing.{" "}
            <span className="italic text-brand-orange">Real journeys.</span>
          </h2>
          <p className="mt-5 text-white/80 text-lg leading-relaxed">
            We've spent eight years earning a reputation we don't take lightly.
          </p>
        </div>

        {/* Stat tiles — unified identical layout for all 4 cards */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_US.map((w, i) => {
            const Icon = ICONS[w.icon];
            return (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                data-testid={`why-card-${i}`}
                className="relative rounded-3xl p-7 glass-dark text-white hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(0,0,0,0.4)] transition-all duration-500 flex flex-col"
              >
                {/* Icon — same position in each card */}
                <div className="h-14 w-14 rounded-2xl bg-brand-orange/20 ring-1 ring-brand-orange/30 flex items-center justify-center shrink-0">
                  <Icon className="w-7 h-7 text-brand-orange" strokeWidth={1.7} />
                </div>

                {/* Large metric number — visually prominent */}
                <div className="mt-5">
                  <div className="font-playfair text-4xl font-bold leading-none text-white">
                    {w.metric}
                  </div>
                  <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mt-1">
                    {w.metricLabel}
                  </div>
                </div>

                {/* Title + description — same structure in each card */}
                <h3 className="mt-5 font-playfair text-xl font-bold leading-snug">
                  {w.title}
                </h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">
                  {w.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Social proof — rating near CTA area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-12 flex justify-center"
        >
          <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl glass-dark">
            <div className="text-center">
              <div className="font-playfair text-3xl font-bold text-brand-orange leading-none">
                4.9★
              </div>
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-white/60 mt-0.5">
                Avg. Rating
              </div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-sm text-white/80 max-w-xs leading-snug">
              Based on reviews from thousands of happy travellers across India
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
